/* eslint-disable react/require-default-props */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";
import { normalizeId } from "./surveyPreviewUtils";

const SurveyPreviewContext = createContext(null);

function readAnswerRecord(record) {
  if (record == null) {
    return { value: undefined, writeIn: "", note: "", images: [] };
  }

  const isAnswerObject =
    typeof record === "object" &&
    !Array.isArray(record) &&
    ("value" in record ||
      "write_in" in record ||
      "note" in record ||
      "image" in record ||
      "type" in record);

  if (isAnswerObject) {
    return {
      value: record.value,
      writeIn: record.write_in ?? "",
      note: record.note ?? "",
      images: record.image ?? [],
    };
  }

  return {
    value: record,
    writeIn: "",
    note: "",
    images: [],
  };
}

export function SurveyPreviewProvider({
  children,
  externalAnswers,
  onSetAnswer,
}) {
  const [localAnswers, setLocalAnswers] = useState({});

  const answers = externalAnswers ?? localAnswers;

  const setAnswer = useCallback(
    (questionId, patch) => {
      if (onSetAnswer) {
        onSetAnswer(questionId, patch);
        return;
      }

      const key = normalizeId(questionId);
      if (!key) return;

      setLocalAnswers((prev) => {
        const current = readAnswerRecord(prev[key]);
        const next = { ...current };

        if (patch?.value !== undefined) {
          next.value = patch.value;
        }
        if (patch?.write_in !== undefined) {
          next.writeIn = patch.write_in;
        }
        if (patch?.note !== undefined) {
          next.note = patch.note;
        }
        if (patch?.image !== undefined) {
          next.images = patch.image ?? [];
        }

        return {
          ...prev,
          [key]: {
            value: next.value,
            write_in: next.writeIn,
            note: next.note,
            image: next.images,
          },
        };
      });
    },
    [onSetAnswer],
  );

  const value = useMemo(() => ({ answers, setAnswer }), [answers, setAnswer]);

  return (
    <SurveyPreviewContext.Provider value={value}>
      {children}
    </SurveyPreviewContext.Provider>
  );
}

SurveyPreviewProvider.propTypes = {
  children: PropTypes.node.isRequired,
  externalAnswers: PropTypes.object,
  onSetAnswer: PropTypes.func,
};

export function useSurveyPreviewQuestionAnswer(questionId) {
  const context = useContext(SurveyPreviewContext);
  const { value, writeIn, note, images } = readAnswerRecord(
    context?.answers?.[normalizeId(questionId)],
  );

  // `extras` carries answer fields that travel with the value, e.g. a Gridspot's
  // gridspot_point. Optional, so existing single-argument callers are unaffected.
  const setValue = useCallback(
    (next, extras) => {
      context?.setAnswer?.(questionId, { value: next, ...(extras || {}) });
    },
    [context, questionId],
  );

  const setWriteIn = useCallback(
    (next) => {
      context?.setAnswer?.(questionId, { write_in: next });
    },
    [context, questionId],
  );

  const setNote = useCallback(
    (next) => {
      context?.setAnswer?.(questionId, { note: next });
    },
    [context, questionId],
  );

  const setImages = useCallback(
    (next) => {
      context?.setAnswer?.(questionId, { image: next ?? [] });
    },
    [context, questionId],
  );

  return {
    value,
    writeIn,
    note,
    images,
    setValue,
    setWriteIn,
    setNote,
    setImages,
    interactive: Boolean(context),
  };
}

export function useSurveyPreviewAnswer(questionId) {
  const { value, setValue, interactive } =
    useSurveyPreviewQuestionAnswer(questionId);
  return [value, setValue, interactive];
}
